/**
 * Client API Service
 *
 * GraphQL queries and mutations for client management operations.
 * This service provides type-safe GraphQL operations for the frontend.
 */

import { gql } from "@apollo/client";

// Fragment for client data
export const CLIENT_FRAGMENT = gql`
  fragment ClientFragment on Client {
    id
    clientId
    clientName
    subscriptionTier
    status
    contactEmail
    contactPhone
    settings {
      maxUsers
      maxStorageGB
      featuresEnabled
      customBranding {
        logoUrl
        primaryColor
        secondaryColor
        companyName
      }
      integrations {
        type
        enabled
        configuration
      }
    }
    adminUsers {
      id
      email
      firstName
      lastName
      status
      lastLoginAt
      createdAt
    }
    statistics {
      totalUsers
      activeUsers
      storageUsedGB
      filesUploaded
      lastActivityAt
      monthlyStats {
        month
        year
        activeUsers
        filesUploaded
        storageUsedGB
      }
    }
    createdAt
    updatedAt
    version
  }
`;

// Fragment for client statistics
export const CLIENT_STATISTICS_FRAGMENT = gql`
  fragment ClientStatisticsFragment on ClientStatistics {
    totalUsers
    activeUsers
    storageUsedGB
    filesUploaded
    lastActivityAt
    monthlyStats {
      month
      year
      activeUsers
      filesUploaded
      storageUsedGB
    }
  }
`;

// Queries
export const GET_CLIENT = gql`
  ${CLIENT_FRAGMENT}
  query GetClient($clientId: ID) {
    getClient(clientId: $clientId) {
      ...ClientFragment
    }
  }
`;

export const LIST_CLIENTS = gql`
  ${CLIENT_FRAGMENT}
  query ListClients($limit: Int, $nextToken: String) {
    listClients(limit: $limit, nextToken: $nextToken) {
      items {
        ...ClientFragment
      }
      nextToken
    }
  }
`;

export const GET_CLIENT_STATISTICS = gql`
  ${CLIENT_STATISTICS_FRAGMENT}
  query GetClientStatistics($clientId: ID!, $filter: ClientStatsFilter) {
    getClientStatistics(clientId: $clientId, filter: $filter) {
      ...ClientStatisticsFragment
    }
  }
`;

// Mutations
export const CREATE_CLIENT = gql`
  ${CLIENT_FRAGMENT}
  mutation CreateClient($input: CreateClientInput!) {
    createClient(input: $input) {
      ...ClientFragment
    }
  }
`;

export const UPDATE_CLIENT = gql`
  ${CLIENT_FRAGMENT}
  mutation UpdateClient($input: UpdateClientInput!) {
    updateClient(input: $input) {
      ...ClientFragment
    }
  }
`;

export const SUSPEND_CLIENT = gql`
  ${CLIENT_FRAGMENT}
  mutation SuspendClient($clientId: ID!) {
    suspendClient(clientId: $clientId) {
      ...ClientFragment
    }
  }
`;

export const ACTIVATE_CLIENT = gql`
  ${CLIENT_FRAGMENT}
  mutation ActivateClient($clientId: ID!) {
    activateClient(clientId: $clientId) {
      ...ClientFragment
    }
  }
`;

// Subscriptions
export const ON_CLIENT_UPDATED = gql`
  ${CLIENT_FRAGMENT}
  subscription OnClientUpdated($clientId: ID) {
    onClientUpdated(clientId: $clientId) {
      ...ClientFragment
    }
  }
`;

// Type definitions for TypeScript
export interface Client {
  id: string;
  clientId: string;
  clientName: string;
  subscriptionTier: "FREE" | "BASIC" | "PROFESSIONAL" | "ENTERPRISE";
  status: "ACTIVE" | "SUSPENDED" | "PENDING" | "CANCELLED";
  contactEmail: string;
  contactPhone?: string;
  settings: {
    maxUsers: number;
    maxStorageGB: number;
    featuresEnabled: string[];
    customBranding?: {
      logoUrl?: string;
      primaryColor?: string;
      secondaryColor?: string;
      companyName?: string;
    };
    integrations: Array<{
      type: string;
      enabled: boolean;
      configuration: any;
    }>;
  };
  adminUsers: Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    status: "ACTIVE" | "INACTIVE" | "PENDING" | "SUSPENDED";
    lastLoginAt?: string;
    createdAt: string;
  }>;
  statistics: {
    totalUsers: number;
    activeUsers: number;
    storageUsedGB: number;
    filesUploaded: number;
    lastActivityAt?: string;
    monthlyStats: Array<{
      month: string;
      year: number;
      activeUsers: number;
      filesUploaded: number;
      storageUsedGB: number;
    }>;
  };
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface CreateClientInput {
  clientName: string;
  contactEmail: string;
  contactPhone?: string;
  subscriptionTier: "FREE" | "BASIC" | "PROFESSIONAL" | "ENTERPRISE";
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
}

export interface UpdateClientInput {
  clientId: string;
  clientName?: string;
  contactEmail?: string;
  contactPhone?: string;
  subscriptionTier?: "FREE" | "BASIC" | "PROFESSIONAL" | "ENTERPRISE";
  status?: "ACTIVE" | "SUSPENDED" | "PENDING" | "CANCELLED";
}

export interface ClientStatsFilter {
  dateFrom?: string;
  dateTo?: string;
  includeUsage?: boolean;
  includeActivity?: boolean;
}

export interface ListClientsResponse {
  listClients: {
    items: Client[];
    nextToken?: string;
  };
}

export interface GetClientResponse {
  getClient: Client;
}

export interface GetClientStatisticsResponse {
  getClientStatistics: Client["statistics"];
}

export interface CreateClientResponse {
  createClient: Client;
}

export interface UpdateClientResponse {
  updateClient: Client;
}

export interface SuspendClientResponse {
  suspendClient: Client;
}

export interface ActivateClientResponse {
  activateClient: Client;
}

// Admin user management types
export interface AddClientAdminInput {
  clientId: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface RemoveClientAdminInput {
  clientId: string;
  userId: string;
}

export interface ClientAdmin {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: "ACTIVE" | "INACTIVE" | "PENDING" | "SUSPENDED";
  lastLoginAt?: string;
  createdAt: string;
}

export interface AddClientAdminResponse {
  addClientAdmin: ClientAdmin;
}

export interface RemoveClientAdminResponse {
  removeClientAdmin: boolean;
}

// Helper functions for client operations
export const getSubscriptionTierLabel = (tier: string): string => {
  const labels = {
    FREE: "Free",
    BASIC: "Basic",
    PROFESSIONAL: "Professional",
    ENTERPRISE: "Enterprise",
  };
  return labels[tier as keyof typeof labels] || tier;
};

export const getSubscriptionTierDescription = (tier: string): string => {
  const descriptions = {
    FREE: "2 users, 1GB storage",
    BASIC: "10 users, 10GB storage",
    PROFESSIONAL: "50 users, 100GB storage",
    ENTERPRISE: "Unlimited users and storage",
  };
  return descriptions[tier as keyof typeof descriptions] || "";
};

export const getStatusColor = (status: string): string => {
  const colors = {
    ACTIVE: "green",
    SUSPENDED: "red",
    PENDING: "yellow",
    CANCELLED: "gray",
  };
  return colors[status as keyof typeof colors] || "gray";
};

export const getTierColor = (tier: string): string => {
  const colors = {
    FREE: "gray",
    BASIC: "blue",
    PROFESSIONAL: "purple",
    ENTERPRISE: "gold",
  };
  return colors[tier as keyof typeof colors] || "gray";
};

export const formatStorageUsage = (usedGB: number, maxGB: number): string => {
  if (maxGB === -1) {
    return `${usedGB.toFixed(1)} GB of unlimited`;
  }
  const percentage = (usedGB / maxGB) * 100;
  return `${usedGB.toFixed(1)} GB of ${maxGB} GB (${percentage.toFixed(1)}%)`;
};

export const formatUserUsage = (used: number, max: number): string => {
  if (max === -1) {
    return `${used} of unlimited users`;
  }
  const percentage = (used / max) * 100;
  return `${used} of ${max} users (${percentage.toFixed(1)}%)`;
};

// Admin user management mutations
export const ADD_CLIENT_ADMIN = gql`
  mutation AddClientAdmin($input: AddClientAdminInput!) {
    addClientAdmin(input: $input) {
      id
      email
      firstName
      lastName
      status
      lastLoginAt
      createdAt
    }
  }
`;

export const REMOVE_CLIENT_ADMIN = gql`
  mutation RemoveClientAdmin($input: RemoveClientAdminInput!) {
    removeClientAdmin(input: $input)
  }
`;
